/**
 * The typed fetch layer.
 *
 * Rules this file exists to enforce:
 *   1. No hand-written URL strings anywhere in the app. Paths come from
 *      `ApiRoutes` in @nav/shared, so a route rename breaks the build, not prod.
 *   2. Every non-2xx response carries the `ApiError` envelope. It is parsed here
 *      once and rethrown as `ApiClientError`, which UI code can inspect for
 *      `code` (e.g. `FUND_NOT_FOUND`) without touching `response.json()`.
 *   3. Cookies ride along (`credentials: 'same-origin'`) so the admin session
 *      works from the SPA without any per-call ceremony.
 */

import {
  ACCEPTED_UPLOAD_EXTENSIONS,
  ApiRoutes,
  ErrorCodes,
  type AuthState,
  type CommitResult,
  type Dataset,
  type Fund,
  type FundSummary,
  type FundsResponse,
  type HealthStatus,
  type HoldingsResponse,
  type InsightDraftsResponse,
  type InsightGenerateResponse,
  type InsightReviewRequest,
  type InsightRunCardsResponse,
  type InsightRunsResponse,
  type InsightsResponse,
  type ResearchedInsight,
  type MarketSnapshot,
  type NavDailyResponse,
  type NavMonthlyResponse,
  type Period,
  type UploadRecord,
  type UploadsResponse,
  type ValidationReport,
} from '@nav/shared';

type Fixtures = typeof import('./__fixtures__/sampleData');
type AdminFixtures = typeof import('./__fixtures__/adminSamples');

// ─── Errors ──────────────────────────────────────────────────────────────────

/** Every failure that leaves this module is one of these. */
export class ApiClientError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: unknown;

  constructor(params: { code: string; message: string; status: number; details?: unknown }) {
    super(params.message);
    this.name = 'ApiClientError';
    this.code = params.code;
    this.status = params.status;
    this.details = params.details;
  }

  /** True for the "this fund has no such thing" family, which the UI treats as empty, not broken. */
  get isNotFound(): boolean {
    return this.code === ErrorCodes.NOT_FOUND || this.code === ErrorCodes.FUND_NOT_FOUND;
  }
}

// ─── Development escape hatch ────────────────────────────────────────────────
//
// `?mock=1`     → serve src/lib/__fixtures__/sampleData.ts instead of fetching.
// `?mock=error` → every call rejects, so the error/retry affordances are testable.
//
// This is a DEV AFFORDANCE for working before the API exists. It is opt-in per
// page load and never activates on its own.

type MockMode = 'off' | 'data' | 'error';

function readMockMode(): MockMode {
  if (typeof window === 'undefined') return 'off';
  const value = new URLSearchParams(window.location.search).get('mock');
  if (value === 'error') return 'error';
  if (value === '1' || value === 'true' || value === 'on') return 'data';
  return 'off';
}

let mockMode: MockMode = readMockMode();

/** True when the page is running against fixtures. Surfaced as a banner in the shell. */
export function isMockMode(): boolean {
  return mockMode !== 'off';
}

export function mockModeKind(): MockMode {
  return mockMode;
}

/** Escape hatch for tests / manual toggling from the console. */
export function setMockMode(mode: MockMode): void {
  mockMode = mode;
}

/** Fixtures resolve on a short timer so loading skeletons are actually visible. */
const MOCK_LATENCY_MS = 260;

/**
 * The fixture module is imported dynamically so it lands in its own chunk that
 * production never requests — the ~35 kB of sample NAV rows stay out of the
 * bundle real users download.
 */
function loadFixtures(): Promise<Fixtures> {
  return import('./__fixtures__/sampleData');
}

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

async function mockResolve<T>(produce: (fixtures: Fixtures) => T): Promise<T> {
  await wait(MOCK_LATENCY_MS);
  if (mockMode === 'error') {
    throw new ApiClientError({
      code: ErrorCodes.INTERNAL,
      message: 'Mock error mode is on (?mock=error). No request was sent.',
      status: 500,
    });
  }
  return produce(await loadFixtures());
}

// ─── Core request ────────────────────────────────────────────────────────────

function buildUrl(path: string, query?: Record<string, string | number | undefined>): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

/** Narrow an unknown JSON body to the `ApiError` envelope. */
function parseErrorEnvelope(body: unknown): { code: string; message: string; details?: unknown } | null {
  if (typeof body !== 'object' || body === null || !('error' in body)) return null;
  const err = (body as { error: unknown }).error;
  if (typeof err !== 'object' || err === null) return null;
  const { code, message, details } = err as Record<string, unknown>;
  if (typeof code !== 'string' || typeof message !== 'string') return null;
  return { code, message, details };
}

async function request<T>(
  path: string,
  options: { query?: Record<string, string | number | undefined>; init?: RequestInit } = {},
): Promise<T> {
  const url = buildUrl(path, options.query);

  let response: Response;
  try {
    response = await fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json', ...(options.init?.headers ?? {}) },
      ...options.init,
    });
  } catch (cause) {
    // Network-level failure: no response at all (server down, offline, CORS).
    throw new ApiClientError({
      code: 'NETWORK_ERROR',
      message: 'Tidak dapat menghubungi server. Periksa koneksi lalu coba lagi.',
      status: 0,
      details: cause,
    });
  }

  if (!response.ok) {
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      /* non-JSON error body (proxy HTML, empty 502…) — fall through to the default */
    }
    const envelope = parseErrorEnvelope(body);
    throw new ApiClientError({
      code: envelope?.code ?? ErrorCodes.INTERNAL,
      message: envelope?.message ?? `Permintaan gagal (HTTP ${response.status}).`,
      status: response.status,
      details: envelope?.details,
    });
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

// ─── Reference data ──────────────────────────────────────────────────────────

export function getHealth(): Promise<HealthStatus> {
  return request<HealthStatus>(ApiRoutes.health());
}

export function getFunds(): Promise<FundsResponse> {
  if (isMockMode()) return mockResolve((fixtures) => fixtures.FUNDS_RESPONSE);
  return request<FundsResponse>(ApiRoutes.funds());
}

export function getBusinessModels(): Promise<FundsResponse['businessModels']> {
  if (isMockMode()) return mockResolve((fixtures) => fixtures.BUSINESS_MODELS);
  return request<FundsResponse['businessModels']>(ApiRoutes.businessModels());
}

export function getFund(code: string): Promise<Fund> {
  if (isMockMode()) {
    return mockResolve((fixtures) => {
      const fund = fixtures.FUNDS.find((f) => f.code === code);
      if (!fund) {
        throw new ApiClientError({
          code: ErrorCodes.FUND_NOT_FOUND,
          message: `Fund ${code} tidak ditemukan.`,
          status: 404,
        });
      }
      return fund;
    });
  }
  return request<Fund>(ApiRoutes.fund(code));
}

export function getFundSummary(code: string): Promise<FundSummary> {
  if (isMockMode()) return mockResolve((fixtures) => fixtures.buildSummary(code));
  return request<FundSummary>(ApiRoutes.fundSummary(code));
}

// ─── Series ──────────────────────────────────────────────────────────────────

export type NavWindow = '6m' | '1y' | '2y' | 'all';

export function getNavMonthly(
  code: string,
  params: { window?: NavWindow; from?: Period; to?: Period } = {},
): Promise<NavMonthlyResponse> {
  if (isMockMode()) return mockResolve((fixtures) => fixtures.buildNavMonthly(code, params.window));
  return request<NavMonthlyResponse>(ApiRoutes.navMonthly(code), {
    query: { window: params.window, from: params.from, to: params.to },
  });
}

export function getNavDaily(
  code: string,
  params: { limit?: number; from?: string; to?: string } = {},
): Promise<NavDailyResponse> {
  if (isMockMode()) return mockResolve((fixtures) => fixtures.buildNavDaily(code, params.limit ?? 30));
  return request<NavDailyResponse>(ApiRoutes.navDaily(code), {
    query: { limit: params.limit, from: params.from, to: params.to },
  });
}

export function getHoldings(code: string, period?: Period): Promise<HoldingsResponse> {
  if (isMockMode()) return mockResolve((fixtures) => fixtures.buildHoldings(code, period));
  return request<HoldingsResponse>(ApiRoutes.holdings(code), { query: { period } });
}

export function getHoldingPeriods(code: string): Promise<Period[]> {
  if (isMockMode()) return mockResolve((fixtures) => fixtures.buildHoldings(code).availablePeriods);
  return request<Period[]>(ApiRoutes.holdingPeriods(code));
}

/**
 * Live USD/IDR + (if the server has a FRED key) US macro figures. Never
 * throws for "provider unavailable" — a snapshot with every field `null` is
 * itself the graceful-degradation signal; the UI falls back to editorial text.
 */
export function getMarketSnapshot(): Promise<MarketSnapshot> {
  if (isMockMode()) return mockResolve((fixtures) => fixtures.MARKET_SNAPSHOT);
  return request<MarketSnapshot>(ApiRoutes.marketSnapshot());
}

// ─── AI insights ─────────────────────────────────────────────────────────────

/**
 * Published, analyst-approved insight cards for one fund.
 *
 * Reads a table on the server; it never triggers an LLM call, so this is an
 * ordinary cheap GET. An empty list is the normal answer when the feature is
 * off, and the page renders its computed cards regardless.
 */
export async function getInsights(code: string): Promise<InsightsResponse> {
  if (isMockMode()) return mockResolve((fixtures) => fixtures.buildInsights(code));
  return request<InsightsResponse>(ApiRoutes.insights(code));
}

/** Admin: the review queue. */
export async function getInsightDrafts(): Promise<InsightDraftsResponse> {
  return request<InsightDraftsResponse>(ApiRoutes.insightDrafts());
}

/** Admin: run ledger plus month-to-date spend against the ceiling. */
export async function getInsightRuns(): Promise<InsightRunsResponse> {
  return request<InsightRunsResponse>(ApiRoutes.insightRuns());
}

/**
 * Admin: every card one run produced — kept and rejected — with the guard's
 * notes. The ledger says "2 of 4 kept"; this says why.
 */
export async function getInsightRunCards(id: number): Promise<InsightRunCardsResponse> {
  return request<InsightRunCardsResponse>(ApiRoutes.insightRunCards(id));
}

/** Admin: approve or refuse one draft. A refusal must carry a reason. */
export async function reviewInsight(
  id: number,
  body: InsightReviewRequest,
): Promise<ResearchedInsight> {
  return request<ResearchedInsight>(ApiRoutes.insightReview(id), {
    init: {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  });
}

/**
 * Admin: start a generation run and WAIT for it.
 *
 * Minutes, not seconds — it does live web research. The server is synchronous by
 * design (see `generate.ts`), so the UI must show a long-running state rather
 * than assume this behaves like the other calls.
 */
export async function generateInsights(): Promise<InsightGenerateResponse> {
  return request<InsightGenerateResponse>(ApiRoutes.insightsGenerate(), {
    init: { method: 'POST' },
  });
}

// ─── Exports ─────────────────────────────────────────────────────────────────
//
// CSV export is a browser navigation, not a fetch: the server owns the file so
// the download always matches the database rather than whatever the page happens
// to be holding in memory.

export function navDailyCsvUrl(code: string): string {
  return ApiRoutes.exportNavDailyCsv(code);
}

export function navMonthlyCsvUrl(code: string): string {
  return ApiRoutes.exportNavMonthlyCsv(code);
}

// ─── Auth ────────────────────────────────────────────────────────────────────

export function getAuthState(): Promise<AuthState> {
  if (isMockMode()) return mockAuthResolve(() => mockAuth);
  return request<AuthState>(ApiRoutes.me());
}

export function login(password: string): Promise<AuthState> {
  if (isMockMode()) return mockLogin(password);
  return request<AuthState>(ApiRoutes.login(), {
    init: {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    },
  });
}

export function logout(): Promise<AuthState> {
  if (isMockMode()) {
    return mockAuthResolve(() => {
      mockAuth = ANONYMOUS;
      return mockAuth;
    });
  }
  return request<AuthState>(ApiRoutes.logout(), { init: { method: 'POST' } });
}

// ─── Uploads (admin) ─────────────────────────────────────────────────────────

export function getUploads(): Promise<UploadsResponse> {
  if (isMockMode()) {
    return mockResolveAdmin(async (fixtures) => ({
      uploads: [...mockCommittedUploads, ...fixtures.MOCK_UPLOADS],
    }));
  }
  return request<UploadsResponse>(ApiRoutes.uploads());
}

export function templateUrl(dataset: Dataset, format: 'csv' | 'xlsx' = 'xlsx'): string {
  return ApiRoutes.template(dataset, format);
}

/**
 * What both upload endpoints take. `dataset` travels in the body rather than the
 * URL because the server reads it alongside the file from the same multipart
 * parse — see `ApiRoutes.uploadValidate` / `uploadCommit`.
 */
export interface UploadInput {
  dataset: Dataset;
  file: File;
}

function uploadFormData({ dataset, file }: UploadInput): FormData {
  const form = new FormData();
  // Field names are part of the contract: `file` and `dataset`.
  form.append('file', file, file.name);
  form.append('dataset', dataset);
  return form;
}

/**
 * Dry run. Writes nothing; returns the report the analyst confirms against.
 * `Content-Type` is deliberately not set — the browser must add the multipart
 * boundary itself.
 */
export function validateUpload(input: UploadInput): Promise<ValidationReport> {
  if (isMockMode()) return mockValidateUpload(input);
  return request<ValidationReport>(ApiRoutes.uploadValidate(), {
    init: { method: 'POST', body: uploadFormData(input) },
  });
}

/**
 * The write. The SAME file is re-sent rather than a report id: the server
 * re-validates from the bytes, so a database that changed between the dry run
 * and the confirmation cannot be written against a stale projection.
 */
export function commitUpload(input: UploadInput): Promise<CommitResult> {
  if (isMockMode()) return mockCommitUpload(input);
  return request<CommitResult>(ApiRoutes.uploadCommit(), {
    init: { method: 'POST', body: uploadFormData(input) },
  });
}

/** Lower-cased extension including the dot, or `''`. */
export function fileExtension(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot < 0 ? '' : filename.slice(dot).toLowerCase();
}

export function isAcceptedUploadFile(filename: string): boolean {
  return (ACCEPTED_UPLOAD_EXTENSIONS as readonly string[]).includes(fileExtension(filename));
}

// ─── Mock: auth ──────────────────────────────────────────────────────────────
//
// Fixture mode keeps a session in module memory so `/login?mock=1` renders the
// real form and the guard on `/admin` behaves exactly as it does against the
// server. Append `&mockAdmin=1` to start already signed in.

const ANONYMOUS: AuthState = { isAdmin: false, user: null, expiresAt: null };

/** The only password fixture mode accepts. Shown on the login page under `?mock=1`. */
export const MOCK_ADMIN_PASSWORD = 'demo';
/** Typing this triggers the 429 branch, so the rate-limit copy is testable. */
export const MOCK_RATE_LIMIT_PASSWORD = 'limit';

function mockSignedIn(): AuthState {
  return {
    isAdmin: true,
    user: 'analis.nav',
    expiresAt: new Date(Date.now() + 8 * 3_600_000).toISOString(),
  };
}

function readMockAdminSeed(): AuthState {
  if (typeof window === 'undefined') return ANONYMOUS;
  const value = new URLSearchParams(window.location.search).get('mockAdmin');
  return value === '1' || value === 'true' ? mockSignedIn() : ANONYMOUS;
}

let mockAuth: AuthState = readMockAdminSeed();

async function mockAuthResolve(produce: () => AuthState): Promise<AuthState> {
  await wait(MOCK_LATENCY_MS);
  if (mockMode === 'error') {
    throw new ApiClientError({
      code: ErrorCodes.INTERNAL,
      message: 'Mock error mode is on (?mock=error). No request was sent.',
      status: 500,
    });
  }
  return produce();
}

async function mockLogin(password: string): Promise<AuthState> {
  await wait(MOCK_LATENCY_MS * 2);
  if (password === MOCK_RATE_LIMIT_PASSWORD) {
    throw new ApiClientError({
      code: ErrorCodes.RATE_LIMITED,
      message: 'Terlalu banyak percobaan masuk. Coba lagi dalam 5 menit.',
      status: 429,
    });
  }
  if (password !== MOCK_ADMIN_PASSWORD) {
    throw new ApiClientError({
      code: ErrorCodes.UNAUTHORIZED,
      message: 'Password salah.',
      status: 401,
    });
  }
  mockAuth = mockSignedIn();
  return mockAuth;
}

/**
 * Drops the fixture session without touching the page, so the mid-session
 * expiry path can be exercised: run `__navExpireMockSession()` in the console,
 * then press any admin button. Exposed only under `?mock=…`, in the same spirit
 * as `setMockMode` above.
 */
export function expireMockSession(): void {
  mockAuth = ANONYMOUS;
}

if (typeof window !== 'undefined' && mockMode !== 'off') {
  (window as unknown as Record<string, unknown>).__navExpireMockSession = expireMockSession;
}

// ─── Mock: uploads ───────────────────────────────────────────────────────────

/** Type-only import: erased at build time, so no fixture code is pulled in. */
export type MockUploadScenarioId = import('./__fixtures__/adminSamples').MockUploadScenarioId;

/**
 * The fixture reports the mock validate endpoint can return, in the order the
 * picker lists them. Kept here (not in the fixture module) so the UI can render
 * the picker without pulling the fixture chunk into the page.
 */
export const MOCK_UPLOAD_SCENARIOS: ReadonlyArray<{
  id: MockUploadScenarioId;
  label: string;
  hint: string;
}> = [
  { id: 'clean', label: 'Bersih — tidak ada perubahan', hint: 'ok, seluruh baris unchanged' },
  { id: 'insert-update', label: '3 baris baru + 1 diperbarui', hint: 'ok, effect campuran' },
  { id: 'warnings', label: 'Hanya peringatan', hint: 'ok walau ada warning' },
  { id: 'errors', label: 'Berisi error baris', hint: 'ok:false — commit terkunci' },
  { id: 'missing-column', label: 'Kolom wajib hilang', hint: 'ok:false, tanpa preview' },
  { id: 'too-many', label: '400+ masalah', hint: 'menguji pemotongan daftar masalah' },
  { id: 'commit-conflict', label: 'Valid, lalu 422 saat commit', hint: 'server berubah pikiran' },
];

let mockUploadScenario: MockUploadScenarioId = 'insert-update';

export function getMockUploadScenario(): MockUploadScenarioId {
  return mockUploadScenario;
}

export function setMockUploadScenario(id: MockUploadScenarioId): void {
  mockUploadScenario = id;
}

const mockCommittedUploads: UploadRecord[] = [];
let mockUploadId = 100;

function loadAdminFixtures(): Promise<AdminFixtures> {
  return import('./__fixtures__/adminSamples');
}

async function mockResolveAdmin<T>(produce: (fixtures: AdminFixtures) => T | Promise<T>): Promise<T> {
  await wait(MOCK_LATENCY_MS);
  if (mockMode === 'error') {
    throw new ApiClientError({
      code: ErrorCodes.INTERNAL,
      message: 'Mock error mode is on (?mock=error). No request was sent.',
      status: 500,
    });
  }
  // These are the admin-only endpoints, so the fixture server enforces the
  // fixture session — that is what makes the expired-session path testable.
  if (!mockAuth.isAdmin) {
    throw new ApiClientError({
      code: ErrorCodes.UNAUTHORIZED,
      message: 'Sesi admin tidak ditemukan atau sudah berakhir.',
      status: 401,
    });
  }
  return produce(await loadAdminFixtures());
}

async function mockValidateUpload(input: UploadInput): Promise<ValidationReport> {
  // Slower than a read: validation genuinely takes a moment, and the progress
  // affordance needs to be visible while eyeballing the flow.
  await wait(MOCK_LATENCY_MS * 3);
  return mockResolveAdmin((fixtures) =>
    fixtures.buildMockReport(mockUploadScenario, input.dataset, input.file.name),
  );
}

async function mockCommitUpload(input: UploadInput): Promise<CommitResult> {
  await wait(MOCK_LATENCY_MS * 3);
  return mockResolveAdmin((fixtures) => {
    if (mockUploadScenario === 'commit-conflict') {
      // The server re-validates and legitimately disagrees with the dry run.
      throw new ApiClientError({
        code: ErrorCodes.VALIDATION_FAILED,
        message: 'Berkas gagal validasi ulang di server. Tidak ada data yang disimpan.',
        status: 422,
        details: fixtures.buildMockReport('errors', input.dataset, input.file.name),
      });
    }
    const report = fixtures.buildMockReport(mockUploadScenario, input.dataset, input.file.name);
    if (!report.ok) {
      throw new ApiClientError({
        code: ErrorCodes.VALIDATION_FAILED,
        message: 'Berkas masih memiliki error. Tidak ada data yang disimpan.',
        status: 422,
        details: report,
      });
    }
    mockUploadId += 1;
    const result = fixtures.buildMockCommitResult(report, mockUploadId);
    mockCommittedUploads.unshift(result.upload);
    return result;
  });
}
